import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Alert,
  Table,
  Input,
  Label,
} from 'reactstrap';
import { toast } from 'react-toastify';
import {
  BtnBold,
  BtnItalic,
  Editor,
  EditorProvider,
  Toolbar,
  BtnBulletList,
  BtnClearFormatting,
  BtnLink,
  BtnNumberedList,
  BtnRedo,
  BtnStrikeThrough,
  BtnStyles,
  BtnUnderline,
  BtnUndo,
  Separator,
} from 'react-simple-wysiwyg';

import ActionButton from '../../../../../Components/Common/ActionButton';
import { downloadSupportingDocument } from '../tenderOpeningApi';
import { formatDate } from '../tenderOpeningFormatters';
import { HeaderTenderCard, CargoRequirementCard } from './TenderInfoCards';
import { previewInvitation, printInvitationPdf } from './tenderInvitationDoc';

// Strip HTML tags so emptiness checks aren't fooled by an "empty" editor that
// still emits markup like <p><br></p> or &nbsp;.
const htmlToText = html =>
  String(html || '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();

// Tender Opening Detail > Sending Invitation to DMUT — layout mengikuti desain
// acuan:
//   1. Informasi Header Tender + Cargo Requirement (kartu bersama, sama dengan
//      tab Sending Request HPS).
//   2. Detail Clause Information — echo klausul Planning, read-only di sini
//      (pengeditan klausul tetap lewat tab Requirement saat On Soft Recall).
//   3. Dokumen Lampiran Undangan (DMUT Tagging) — supporting documents yang
//      ditandai showToDmut, bisa diunduh.
//   4. Mitra Usaha Terundang (Invited DMUT) — daftar DMUT/PIC dari Planning.
//   5. Footer: Kembali ke List, Soft Recall, Sending Invitation to DMUT
//      (kontrak endpoint 5 — no body; recipients diselesaikan server-side).
const SendInvitationDmutTab = ({
  record,
  detail,
  sent,
  editable,
  onSend,
  sending,
  canSoftRecall,
  onSoftRecall,
  recipients,
  loadingRecipients,
  onBack,
  // Alur Soft Recall (pindahan dari tab Requirement yang dihapus): saat status
  // On Soft Recall, clause bisa diedit di sini dan Resubmit Clause (kontrak
  // endpoint 10) menjadi jalan keluarnya.
  clauseEditable = false,
  onResubmitClauses,
  resubmitting = false,
}) => {
  const rows = recipients || [];
  const hasAnyPic = rows.some(r => (r.pics || []).length > 0);
  const cargos = detail?.requirement?.cargos || [];
  const clauses = detail?.requirement?.clauses || [];
  const softRecall = detail?.softRecall || {};

  // Salinan clause yang bisa diedit — hanya berarti saat clauseEditable.
  // Re-sync tiap Detail reload (initial load atau setelah Resubmit sukses).
  const [clauseForm, setClauseForm] = useState(clauses);
  useEffect(() => {
    setClauseForm(detail?.requirement?.clauses || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.requirement?.clauses]);

  const setClauseField = (clauseId, key, value) =>
    setClauseForm(prev =>
      prev.map(c => (c.clauseId === clauseId ? { ...c, [key]: value } : c))
    );

  const handleResubmit = () => {
    if (clauseForm.some(c => !htmlToText(c.summernote))) {
      toast.error('Summary Note is required for every clause before Resubmit.');
      return;
    }
    onResubmitClauses(clauseForm);
  };
  // Hanya dokumen yang di-tag "Show to DMUT" yang dilampirkan ke undangan.
  const taggedDocs = (detail?.supportingDocuments || []).filter(
    d => d.showToDmut
  );

  // Flatten DMUT -> PIC: one row per PIC (a DMUT can have many PICs).
  const vendorRows = rows.flatMap(r =>
    (r.pics || []).length === 0
      ? [{ key: r.id, dmutName: r.dmutName, picName: '', picEmail: '' }]
      : r.pics.map(p => ({
          key: `${r.id}-${p.id}`,
          dmutName: r.dmutName,
          picName: p.picName || '',
          picEmail: p.picEmail || '',
        }))
  );

  const handleDownloadDoc = async doc => {
    try {
      const ownerId =
        doc.origin === 'planning'
          ? record.planningId
          : record.procurementValidityId;
      const blob = await downloadSupportingDocument(
        doc.origin,
        ownerId,
        doc.id
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.fileName || doc.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(`Download failed: ${error}`);
    }
  };

  const handleSend = () => onSend();

  return (
    <div>
      {/* ---- 1. Informasi Header Tender + Cargo Requirement (bersama) ---- */}
      <HeaderTenderCard record={record} detail={detail} />
      <CargoRequirementCard cargos={cargos} />

      {/* ---- 2. Detail Clause Information (editable saat On Soft Recall) ---- */}
      <Card className='border shadow-none mb-3'>
        <CardHeader className='bg-light d-flex align-items-start flex-wrap gap-2'>
          <div className='flex-grow-1'>
            <span className='fw-semibold'>
              <i className='ri-article-line align-middle me-2'></i>
              Detail Clause Information
            </span>
            <p className='text-muted fs-12 mb-0 mt-1'>
              Detail informasi klausul &amp; syarat pengadaan (Demurrage Claim,
              Sanction Declaration, GTC, dll) dari tahap Planning.
            </p>
          </div>
          {clauseEditable && (
            <span className='badge bg-warning-subtle text-warning fs-11'>
              On Soft Recall — editable until Resubmit
            </span>
          )}
        </CardHeader>
        <CardBody>
          {clauseEditable && softRecall.reason && (
            <Alert color='warning' className='mb-3'>
              <i className='ri-error-warning-line align-middle me-2'></i>
              <span className='fw-semibold'>Soft Recall reason:</span>{' '}
              {softRecall.reason}
            </Alert>
          )}
          {clauseForm.length === 0 ? (
            <p className='text-muted fst-italic mb-0'>No clauses available.</p>
          ) : (
            clauseForm.map(c => (
              <div className='border rounded p-3 mb-2' key={c.clauseId}>
                <div className='d-flex align-items-center flex-wrap gap-2 mb-2'>
                  <span className='fw-semibold text-uppercase flex-grow-1'>
                    {c.title}
                  </span>
                  <small className='text-muted'>
                    Is mandatory for Registered Supplier?
                  </small>
                  {clauseEditable ? (
                    <div className='form-check form-switch mb-0'>
                      <Input
                        type='checkbox'
                        role='switch'
                        className='form-check-input'
                        checked={!!c.mandatory}
                        onChange={e =>
                          setClauseField(
                            c.clauseId,
                            'mandatory',
                            e.target.checked
                          )
                        }
                      />
                      <Label className='form-check-label fs-13'>
                        {c.mandatory ? 'Yes' : 'No'}
                      </Label>
                    </div>
                  ) : (
                    <span
                      className={`badge ${
                        c.mandatory
                          ? 'bg-primary-subtle text-primary'
                          : 'bg-secondary-subtle text-secondary'
                      } border`}
                    >
                      {c.mandatory ? 'Yes' : 'No'}
                    </span>
                  )}
                </div>
                {clauseEditable ? (
                  <EditorProvider>
                    <Editor
                      value={c.summernote || ''}
                      onChange={e =>
                        setClauseField(c.clauseId, 'summernote', e.target.value)
                      }
                      style={{ height: '180px', overflowY: 'auto' }}
                      className='form-control bg-white'
                    >
                      <Toolbar>
                        <BtnStyles />
                        <BtnBold />
                        <BtnItalic />
                        <BtnUnderline />
                        <BtnStrikeThrough />
                        <Separator />
                        <BtnBulletList />
                        <BtnNumberedList />
                        <Separator />
                        <BtnLink />
                        <Separator />
                        <BtnUndo />
                        <BtnRedo />
                        <Separator />
                        <BtnClearFormatting />
                      </Toolbar>
                    </Editor>
                  </EditorProvider>
                ) : (
                  <div
                    className='form-control bg-light'
                    style={{ minHeight: 64, overflow: 'auto' }}
                    dangerouslySetInnerHTML={{
                      __html:
                        c.summernote || '<p class="text-muted mb-0">-</p>',
                    }}
                  />
                )}
              </div>
            ))
          )}
          {clauseEditable && (
            <div className='d-flex justify-content-end mt-3'>
              <ActionButton
                variant='secondary'
                icon='ri-refresh-line'
                disabled={resubmitting}
                onClick={handleResubmit}
              >
                {resubmitting ? 'Resubmitting...' : 'Resubmit Clause'}
              </ActionButton>
            </div>
          )}
        </CardBody>
      </Card>

      {/* ---- 3. Dokumen Lampiran Undangan (DMUT Tagging) ---- */}
      <Alert color='info' className='d-flex align-items-center'>
        <i className='ri-information-line fs-16 me-2'></i>
        Pastikan dokumen prasyarat penawaran dan daftar Mitra Usaha Terseleksi
        (Registered Supplier) terundang telah sesuai sebelum mengirimkan
        undangan tender.
      </Alert>

      <Card className='border shadow-none mb-3'>
        <CardHeader className='bg-light d-flex align-items-center'>
          <span className='fw-semibold flex-grow-1'>
            <i className='ri-attachment-2 align-middle me-2'></i>
            Dokumen Lampiran Undangan (Registered Supplier Tagging)
          </span>
          <span className='badge bg-success-subtle text-success border'>
            Hanya Dokumen Tagging View Registered Supplier
          </span>
        </CardHeader>
        <CardBody className='p-0'>
          <div className='table-responsive'>
            <Table className='table-sm align-middle table-nowrap mb-0'>
              <thead className='table-light'>
                <tr className='text-uppercase fs-12 text-muted'>
                  <th style={{ width: 60 }}>No</th>
                  <th>Nama Dokumen</th>
                  <th>Asal Sumber</th>
                  <th>Terakhir Diperbarui</th>
                  <th className='text-center'>Akses Registered Supplier</th>
                  <th className='text-center'>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {taggedDocs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className='text-center text-muted py-3'>
                      Tidak ada dokumen yang di-tag untuk Registered Supplier
                    </td>
                  </tr>
                ) : (
                  taggedDocs.map((d, idx) => (
                    <tr key={d.id}>
                      <td className='text-muted'>{idx + 1}</td>
                      <td>
                        <i className='ri-file-text-line text-primary me-2'></i>
                        <span className='fw-semibold'>{d.name}</span>
                      </td>
                      <td className='text-uppercase fs-12'>
                        {d.source === 'master'
                          ? 'Master Document'
                          : 'Upload Custom'}
                      </td>
                      <td className='font-monospace fs-12'>
                        {formatDate(d.lastUpdated)}
                      </td>
                      <td className='text-center'>
                        <span className='badge bg-success-subtle text-success border'>
                          Visible to Registered Supplier
                        </span>
                      </td>
                      <td className='text-center'>
                        <ActionButton
                          variant='secondary'
                          size='sm'
                          icon='ri-download-2-line'
                          onClick={() => handleDownloadDoc(d)}
                        >
                          Download
                        </ActionButton>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>

      {/* ---- 4. Mitra Usaha Terundang (Invited DMUT) ---- */}
      <Card className='border shadow-none mb-3'>
        <CardHeader className='bg-light d-flex align-items-center'>
          <span className='fw-semibold flex-grow-1'>
            <i className='ri-building-line align-middle me-2'></i>
            Mitra Usaha Terundang (Invited Registered Supplier)
          </span>
          <span className='badge bg-primary-subtle text-primary border'>
            Total Registered Supplier: {rows.length} Vendor
          </span>
        </CardHeader>
        <CardBody className='p-0'>
          {loadingRecipients ? (
            <p className='text-muted fs-12 m-3'>Loading recipients...</p>
          ) : rows.length === 0 || !hasAnyPic ? (
            <Alert color='warning' className='m-3'>
              <i className='ri-error-warning-line align-middle me-2'></i>
              No Registered Supplier recipients with a PIC were selected during
              Planning — the invitation cannot be sent until at least one is
              added there.
            </Alert>
          ) : (
            <div className='table-responsive'>
              <Table className='table-sm align-middle table-nowrap mb-0'>
                <thead className='table-light'>
                  <tr className='text-uppercase fs-12 text-muted'>
                    <th style={{ width: 60 }}>No</th>
                    <th>Nama Mitra Usaha (Registered Supplier)</th>
                    <th>Nama PIC</th>
                    <th>Email PIC</th>
                    <th className='text-center'>Akses Undangan</th>
                  </tr>
                </thead>
                <tbody>
                  {vendorRows.map((v, idx) => (
                    <tr key={v.key}>
                      <td className='text-muted'>{idx + 1}</td>
                      <td>
                        <i className='ri-building-line text-muted me-2'></i>
                        <span className='fw-semibold'>{v.dmutName}</span>
                      </td>
                      <td>{v.picName || '-'}</td>
                      <td>
                        <span className='font-monospace text-primary fs-12'>
                          {v.picEmail || '-'}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span
                          className={`badge border ${
                            sent
                              ? 'bg-success-subtle text-success'
                              : 'bg-light text-muted'
                          }`}
                        >
                          {sent ? 'Invitation Sent' : 'Ready for Dispatch'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* ---- 5. Footer actions ---- */}
      <div className='d-flex flex-wrap gap-2 align-items-center'>
        <ActionButton
          variant='secondary'
          icon='ri-arrow-left-line'
          onClick={onBack}
        >
          Kembali ke List
        </ActionButton>
        <div className='flex-grow-1'></div>
        <ActionButton
          variant='destructive'
          confirm={false}
          icon='ri-arrow-go-back-line'
          disabled={!canSoftRecall}
          onClick={onSoftRecall}
          title={canSoftRecall ? undefined : 'Not available'}
        >
          Soft Recall
        </ActionButton>
        {/* Dokumen undangan dibangun client-side dari data tab ini (lihat
            tenderInvitationDoc.js) — .PDF lewat dialog print (Save as PDF). */}
        <ActionButton
          variant='secondary'
          icon='ri-eye-line'
          onClick={() => previewInvitation({ record, detail })}
        >
          Preview Dokumen
        </ActionButton>
        <ActionButton
          variant='secondary'
          icon='ri-file-pdf-2-line'
          onClick={() => printInvitationPdf({ record, detail })}
        >
          .PDF
        </ActionButton>
        <ActionButton
          variant='primary'
          icon='ri-send-plane-line'
          disabled={!editable || sent || sending}
          title={editable ? undefined : 'View only'}
          onClick={handleSend}
        >
          {sent
            ? 'Invitation Registered Supplier Terkirim'
            : sending
              ? 'Mengirim...'
              : 'Sending Invitation to Registered Supplier'}
        </ActionButton>
      </div>
    </div>
  );
};

export default SendInvitationDmutTab;
